import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import { Header, Footer } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Mail, MapPin, Phone, Sparkles, Users, ArrowRight } from "lucide-react";

const ContactPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/contact-page`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setData)
      .finally(() => setLoading(false));
  }, []);

  const members = (data?.team_members || []).slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
  const hasTeam = members.length > 0;

  return (
    <div className="min-h-screen flex flex-col bg-surface-app" data-testid="contact-page">
      <Header />
      <main className="flex-1">
        {/* Hero */}
        <section className="bg-gradient-to-br from-[#0F1B3D] via-[#1E3A8A] to-[#0A1A4A] text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#FFD507]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="max-w-4xl mx-auto px-6 py-16 relative">
            <p className="text-[#FFD507] text-xs uppercase tracking-[0.25em] font-bold mb-3">Contact Us</p>
            <h1 className="text-4xl sm:text-5xl font-heading font-bold leading-tight" data-testid="contact-heading">
              {data?.heading || "Meet the Aspiritech Academy team"}
            </h1>
            <p className="text-slate-300 mt-4 max-w-2xl text-base" data-testid="contact-subheading">
              {data?.subheading || "We're a small group dedicated to making tech careers accessible."}
            </p>
            <div className="flex flex-wrap gap-4 mt-8 text-sm">
              {data?.general_email && (
                <a href={`mailto:${data.general_email}`} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-4 py-2" data-testid="contact-email">
                  <Mail className="w-4 h-4 text-[#FFD507]" /> {data.general_email}
                </a>
              )}
              {data?.general_phone && (
                <a href={`tel:${data.general_phone}`} className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg px-4 py-2" data-testid="contact-phone">
                  <Phone className="w-4 h-4 text-[#FFD507]" /> {data.general_phone}
                </a>
              )}
              {data?.address && (
                <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-lg px-4 py-2" data-testid="contact-address">
                  <MapPin className="w-4 h-4 text-[#FFD507]" /> {data.address}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="max-w-5xl mx-auto px-6 py-14">
          {loading ? (
            <p className="text-slate-500 text-center">Loading team…</p>
          ) : hasTeam ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="team-grid">
              {members.map((m) => (
                <Card key={m.id} className="border border-slate-200 hover:border-[#1E3A8A]/40 transition" data-testid={`team-member-${m.id}`}>
                  <CardContent className="p-6 flex flex-col items-center text-center">
                    <Avatar className="w-24 h-24 border-2 border-[#FFD507]/30">
                      {m.image_url ? <AvatarImage src={m.image_url} alt={m.name} /> : null}
                      <AvatarFallback className="bg-[#1E3A8A] text-white text-2xl">
                        {(m.name || "?").charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <h3 className="text-lg font-heading font-bold text-slate-900 mt-4">{m.name}</h3>
                    {m.title && <p className="text-sm text-[#1E3A8A] font-medium">{m.title}</p>}
                    {m.coming_soon && (
                      <span className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-[#0F1B3D] bg-[#FFD507] px-2 py-0.5 rounded">
                        <Sparkles className="w-3 h-3" /> Coming soon
                      </span>
                    )}
                    {m.description && <p className="text-sm text-slate-600 mt-3 leading-relaxed">{m.description}</p>}
                    {m.email && !m.coming_soon && (
                      <a href={`mailto:${m.email}`} className="mt-4 text-sm text-[#1E3A8A] hover:underline inline-flex items-center gap-1" data-testid={`team-member-email-${m.id}`}>
                        <Mail className="w-3.5 h-3.5" /> {m.email}
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="border-dashed border-2 border-slate-200" data-testid="contact-coming-soon">
              <CardContent className="p-12 text-center">
                <div className="w-14 h-14 rounded-full bg-[#FFD507]/20 mx-auto flex items-center justify-center mb-4">
                  <Users className="w-7 h-7 text-[#1E3A8A]" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-slate-900">Team profiles coming soon</h2>
                <p className="text-slate-500 mt-2 max-w-md mx-auto">
                  We're finalising our team & instructor bios. Reach out anytime at
                  <a href={`mailto:${data?.general_email || "academy@aspiritech.org"}`} className="text-[#1E3A8A] hover:underline ml-1">
                    {data?.general_email || "academy@aspiritech.org"}
                  </a>.
                </p>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Secondary CTA */}
        <section className="bg-surface-muted py-12">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-2xl font-heading font-bold text-slate-900">Want to share your skills with employers?</h2>
            <p className="text-slate-600 mt-2">Join the Talent Database — no Academy enrollment required.</p>
            <Link to="/talent-signup" className="inline-flex items-center gap-1 mt-5 font-semibold text-[#1E3A8A] hover:underline" data-testid="contact-talent-cta">
              Create a Talent profile <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
